/**
 * useDraftPresence — heartbeat the current draft + observe other editors.
 *
 * Does NOT do real-time sync. The backend has no WebSocket; this is a
 * 60-second polling loop that:
 *
 *   1. POSTs /api/drafts/{id}/presence so other tabs see "we're here"
 *   2. GETs /api/drafts/{id} to read the current ``editing_user_id``
 *      and decide whether to show "Someone else is editing" UI.
 *
 * Why 60s: cheap enough that two people overlapping for ~30s see each
 * other before the second person types anything destructive. Fine-grained
 * enough that switching drafts feels immediate (presence clears on
 * unmount via the cache key, not via an explicit "I'm gone" call).
 *
 * Why not WebSocket: there is no server-side pub/sub. Adding one is the
 * step-2 work in the planning thread (Yjs / Hocuspocus) and out of scope
 * for this safety net.
 *
 * Gated by the caller — only mount this when ``isTeamMode`` is true.
 */
import { useEffect, useState } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import type { Draft } from '@/types/api'

/** Heartbeats older than this are treated as "no one is here". Matches
 *  the convention documented in the migration. */
const STALE_AFTER_MS = 5 * 60 * 1000
const HEARTBEAT_INTERVAL_MS = 60 * 1000

export interface OtherEditor {
  user_id: string
  /** Milliseconds since the heartbeat. UI uses this for "2 min ago". */
  age_ms: number
}

/**
 * @param draftId — null pauses the loop (between drafts / on logout).
 * @param currentUserId — used to filter ourselves out of the banner.
 * @param enabled — false skips both heartbeat and polling. Pass
 *                  ``isTeamMode`` here so solo users incur zero network
 *                  cost.
 */
export function useDraftPresence(
  draftId: string | null,
  currentUserId: string | null,
  enabled: boolean,
): OtherEditor | null {
  const [other, setOther] = useState<OtherEditor | null>(null)

  useEffect(() => {
    if (!enabled || !draftId || !currentUserId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clearing presence when the hook is disabled / draft switches is the whole point; no external system to subscribe to
      setOther(null)
      return
    }

    let cancelled = false

    const tick = async () => {
      // GET first, POST second. ``editing_user_id`` is a single column
      // (last-write-wins), so our POST clobbers it with our own id —
      // if we wrote first, we'd never observe the other editor. Reading
      // before writing means each side sees the other within ~60s as
      // long as their ticks aren't perfectly synchronized. The worst
      // case is one missed cycle, not permanent invisibility.
      let row: Draft | null = null
      try {
        const res = await apiFetch(`/api/drafts/${draftId}`)
        if (res.ok) row = (await res.json()) as Draft
      } catch {
        // Network blip — try the heartbeat anyway; we'll catch up
        // on the next tick.
      }

      try {
        await apiFetch(`/api/drafts/${draftId}/presence`, {
          method: 'POST',
          body: JSON.stringify({}),
        })
      } catch {
        // Heartbeat is best-effort.
      }

      if (cancelled) return

      if (
        row &&
        row.editing_user_id &&
        row.editing_user_id !== currentUserId &&
        row.editing_since
      ) {
        const age = Date.now() - new Date(row.editing_since).getTime()
        if (age >= 0 && age < STALE_AFTER_MS) {
          setOther({ user_id: row.editing_user_id, age_ms: age })
          return
        }
      }
      setOther(null)
    }

    tick()
    const interval = setInterval(tick, HEARTBEAT_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [draftId, currentUserId, enabled])

  return other
}
